import bootstrap5Plugin from '@fullcalendar/bootstrap5';
import { EventClickArg, EventContentArg, EventInput } from '@fullcalendar/core';
import { EventImpl } from '@fullcalendar/core/internal';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import FullCalendar from '@fullcalendar/react';
import { ResourceApi, ResourceInput } from '@fullcalendar/resource';
import resourceTimelinePlugin from '@fullcalendar/resource-timeline';
import timeGridPlugin from '@fullcalendar/timegrid';
import {
    alpha,
    Box,
    Button,
    lighten,
    Popover,
    Typography,
    useMediaQuery,
} from '@mui/material';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import invariant from 'tiny-invariant';
import { selectReservationFilters } from '../../../../app/Slices/ReservationFiltersSlice';
import {
    useGetAllReservationsQuery,
    useGetIndividualOwnerAssetsQuery,
} from '../../../../app/Slices/SevenDayApiSlice';
import '../../../../layout/styledcomponents/FullCalendar.css';
import LoadingComponent from '../../../../layout/styledcomponents/LoadingAnimations/LoadingComponent';
import UserRole from '../../../../shared/AuthRoles';
import { theme } from '../../../../shared/theme';
import { camelCaseToTitleCase } from '../../../../shared/utils';
import { Status } from '../../../enums/booking';
import { ExperienceType } from '../../../enums/experience';
import { FormValues as Experience } from '../../../types/experience';
import { Link } from '@mui/icons-material';

const css = `
    .btn {
        font-family: ${theme.typography.fontFamily};
    }

    .fc-list-day-text {
        color: ${theme.palette.text.primary} !important
    }

    .fc-list-day-side-text {
        color: ${theme.palette.text.primary} !important
    }
        
    .fc-list-day-cushion {
        background-color: ${lighten(theme.palette.primary.main, 0.9)}
    }

    .fc-event {
        border-radius: 8px;
        padding: 8px;
    }

    .fc-timeline-slot {
        color: ${theme.palette.text.primary} !important
    }

    .fc-datagrid-cell-main {
        color: ${theme.palette.text.primary} !important
    }

    .btn,
    .btn-primary {
        color: ${theme.palette.primary.main} !important;
        background-color: white !important;
        border-color: ${theme.palette.primary.main} !important;
        outline: none !important;
        box-shadow: none !important;
    }

    .btn-primary.hover,
    .btn-primary.active,
    .btn-primary.focus {
        color: white !important;
        background-color: ${theme.palette.primary.main} !important;
        transition: all 0.2s ease;
    }
        
    .fc-toolbar-title {
        color: ${theme.palette.text.primary} !important;
        margin-bottom: 10 !important;
    }
    .fc-col-header-cell {
        color: ${theme.palette.text.primary} !important;
    }
    .fc-daygrid-day-frame {
        color: ${theme.palette.text.primary} !important;
    }
    .fc-day-today {
        background-color: ${lighten(
            theme.palette.primary.main,
            0.9,
        )} !important;
        /* opacity: 0.1; */
    }

    @media (max-width: 767.98px) {
        .fc-toolbar.fc-header-toolbar {
            display: block;
            text-align: left;
        }
        .fc-header-toolbar .fc-toolbar-chunk {
            display: block;
        }
        .fc-toolbar.fc-footer-toolbar {
            display: block;
            text-align: left;
        }
        .fc-footer-toolbar .fc-toolbar-chunk {
            display: block;
        }

        #fc-dom-86 {
            font-family: ${theme.typography.fontFamily};
        }
        .btn-group {
            font-family: ${theme.typography.fontFamily};
            min-width: 360px;
            max-width: 400px;
        }
    }
`;

export default function ViewReservationsCalendarComponent() {
    const isMobile = useMediaQuery('(max-width: 770px)');
    const loggedInUser = UserRole();
    const reservationFilters = useSelector(selectReservationFilters);

    const { data: reservations, isFetching: isFetchingReservations } =
        useGetAllReservationsQuery({
            ...reservationFilters,
            sort: 'startDate',
        });

    const { data: modules, isFetching: isFetchingModules } =
        useGetIndividualOwnerAssetsQuery(
            {
                url: `owners/${loggedInUser?.company}/modules`,
                status: 'published',
            },
            { skip: !loggedInUser?.role.includes('owner') },
        );

    const isCalendarContentLoading =
        isFetchingReservations ||
        (loggedInUser?.role.includes('owner') && isFetchingModules);

    const resources: ResourceInput[] =
        modules?.map((module: Experience) => ({
            id: module.id,
            title: module.name,
            ...(module.type === ExperienceType.Commercial
                ? {
                      children: [
                          {
                              id: `desk${module.id}`,
                              title: 'Desks',
                          },
                          ...module.spaces.map((space: any) => ({
                              id: `s${space.id}`,
                              title: camelCaseToTitleCase(
                                  space.assignedName ?? space.name,
                              ),
                          })),
                      ],
                  }
                : {}),
        })) ?? [];

    type View =
        | 'dayGridMonth'
        | 'timeGridWeek'
        | 'timeGridDay'
        | 'listWeek'
        | 'resourceTimelineMonth'
        | 'resourceTimelineDay'
        | 'resourceTimelineWeek';

    const [view, setView] = useState<View>(
        (sessionStorage.getItem('calendarView') as View) || 'dayGridMonth',
    );
    const [date, setDate] = useState(
        sessionStorage.getItem('calendarDate') || moment().format('YYYY-MM-DD'),
    );

    const [collapsed, setCollapsed] = useState<Record<number, boolean>>({});

    const changeView = (value: View, el: HTMLElement) => {
        if (value === null) return;
        invariant(calendarRef.current);

        const calendar = calendarRef.current.getApi();
        calendar.changeView(value);
        setView(value);
    };

    const handleCollapse = (resource: ResourceApi, value: boolean) => {
        const parent = resource.getParent();
        if (!parent) return;

        setCollapsed((prev) => ({
            ...prev,
            [parent.id]: value,
        }));
    };

    const handleDatesSet = () => {
        const api = calendarRef.current?.getApi();
        if (!api) return;

        const centerDate = moment(api.getDate()).format('YYYY-MM-DD');
        const view = api.view.type;
        setDate(centerDate);
        setView(view);
        sessionStorage.setItem('calendarDate', centerDate);
        sessionStorage.setItem('calendarView', view);
    };

    const navigate = useNavigate();
    const calendarRef: any = useRef(null);

    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [selectedEvent, setSelectedEvent] = useState<EventImpl | null>(null);

    const handleEventClick = (clickInfo: EventClickArg) => {
        setSelectedEvent(clickInfo.event);

        if (view.includes('resourceTimeline')) {
            setAnchorEl(clickInfo.el);
            return;
        }
        handleNavigate(clickInfo.event);
    };

    useEffect(() => {
        // add active class to customButtons, should be able to replace after V7 released
        const calendarBtn = document.querySelector('.fc-calendar-button');
        const resourceBtn = document.querySelector('.fc-resource-button');

        calendarBtn?.classList.toggle(
            'active',
            !view.includes('resourceTimeline'),
        );

        resourceBtn?.classList.toggle(
            'active',
            view.includes('resourceTimeline'),
        );
    }, [view]);

    const handleNavigate = (event: EventImpl | null) => {
        invariant(event);

        const groupId = event._def.groupId;

        const moduleName = event?._def.extendedProps.moduleName
            .replace(/ /g, '')
            .toLowerCase();

        navigate(`/reservations/${moduleName}/${groupId}`, {
            state: {
                name: 'viewReservation',
                type: 'reservations',
                from: 'reservations',
            },
        });

        setAnchorEl(null);
        setSelectedEvent(null);
    };

    const getColors = (value: any) => {
        if (value.bookerType === 'owner' && !value.onBehalf) {
            return {
                backgroundColor: theme.palette.calendar.owner,
                textColor: theme.palette.text.primary,
            };
        }

        if (value.bookerType === 'owner' && value.onBehalf) {
            return {
                backgroundColor: theme.palette.calendar.onBehalf,
                textColor: theme.palette.text.primary,
                borderColor: 'white',
            };
        }

        if (value.status === Status.Completed) {
            return {
                backgroundColor: theme.palette.calendar.completed,
                textColor: theme.palette.text.primary,
                borderColor: 'white',
            };
        }

        if (value.status === Status.Pending) {
            return {
                backgroundColor: theme.palette.calendar.pending,
                textColor: theme.palette.text.secondary,
                borderColor: theme.palette.text.secondary,
            };
        }

        switch (value.module.type) {
            case ExperienceType.Property:
                return {
                    backgroundColor: theme.palette.calendar.property,
                    textColor: 'white',
                    borderColor: 'white',
                };
            case ExperienceType.Boat:
                return {
                    backgroundColor: theme.palette.calendar.boat,
                    textColor: 'white',
                    borderColor: 'white',
                };
            case ExperienceType.Activity:
                return {
                    backgroundColor: theme.palette.calendar.activity,
                    textColor: theme.palette.text.primary,
                    borderColor: 'white',
                };
            case ExperienceType.Vehicle:
                return {
                    backgroundColor: theme.palette.calendar.vehicle,
                    textColor: 'white',
                    borderColor: 'white',
                };
            case ExperienceType.Commercial:
                return {
                    backgroundColor: theme.palette.calendar.commercial,
                    textColor: 'white',
                    borderColor: 'white',
                };
            default:
                return {
                    backgroundColor: theme.palette.calendar.calendarDefault,
                    textColor: 'white',
                    borderColor: 'white',
                };
        }
    };

    const getDates = reservations
        ?.filter(
            (value: any) =>
                value?.status !== 'cancelled' && value?.status !== 'declined',
        )
        ?.flatMap((value: any) => {
            const start = value?.bookingInfo?.checkinTime
                ? new Date(
                      `${value?.startDate}T${value?.bookingInfo?.checkinTime}:00`,
                  )
                : value?.startDate;

            const end = value?.bookingInfo?.checkoutTime
                ? new Date(
                      `${value?.endDate}T${value?.bookingInfo?.checkoutTime}:00`,
                  )
                : new Date(new Date(value?.endDate).setHours(24));

            const display =
                view.includes('resourceTimeline') &&
                value?.module?.type === ExperienceType.Commercial
                    ? value?.bookingInfo?.entireVenue ||
                      collapsed[value?.module?.id]
                        ? 'default'
                        : 'none'
                    : 'default';

            const { backgroundColor, textColor, borderColor } =
                getColors(value);

            const events: EventInput[] = [
                {
                    groupId: value?.id,
                    resourceId: value?.module.id,
                    id: value?.id,
                    moduleName: value?.module.name,
                    display,
                    allDay: value?.bookerType === 'owner' && !value?.onBehalf,
                    title:
                        value?.bookerType === 'owner' &&
                        value?.onBehalf === false
                            ? `${value?.module.name} - REF: #${
                                  value?.id
                              } BLOCKED ${value?.reason
                                  .replace(/([A-Z])/g, ' $1')
                                  .replace(/^./, (str: string) =>
                                      str.toUpperCase(),
                                  )}`
                            : `${value?.module.name} - REF: #${value?.id} ${
                                  value?.bookerType === 'guest'
                                      ? value.guest?.name
                                      : value.owner?.name
                              } (${value?.status})`,
                    start,
                    extendedProps: {
                        status: value?.status,
                        type: value?.module?.type,
                    },
                    end,
                    backgroundColor,
                    textColor,
                    borderColor,
                },
            ];

            const childEvents: EventInput[] = value?.spaces.map(
                (space: any) => ({
                    id: `s${space.id}r${value?.id}t${space.startDate}`,
                    groupId: value?.id,
                    resourceId: `s${space.id}`,
                    moduleName: value?.module.name,
                    title: `REF: #${value.id} - booked by ${value.booker?.firstName} from ${value.guest?.name} (${value.status})`,
                    start:
                        new Date(space.startDate) <= new Date(value?.startDate)
                            ? start
                            : new Date(new Date(space.startDate).setHours(0)),
                    end:
                        new Date(space.endDate) >= new Date(value?.endDate)
                            ? end
                            : new Date(new Date(space.endDate).setHours(24)),
                    textColor,
                    backgroundColor,
                    borderColor,
                }),
            );

            if (
                value?.module?.type === ExperienceType.Commercial &&
                value?.bookingInfo?.desks === 1
            ) {
                childEvents.push({
                    id: `desk${value?.id}`,
                    groupId: value?.id,
                    moduleName: value?.module.name,
                    resourceId: `desk${value?.module?.id}`,
                    title: `REF: #${value.id} - booked by ${value.booker?.firstName} from ${value.guest?.name} (${value.status})`,
                    start,
                    end,
                    textColor,
                    backgroundColor,
                    borderColor,
                });
            }

            if (value?.linkedModules.length > 0) {
                value.linkedModules.map((module: any) => {
                    if (
                        module.linkType === 'block' &&
                        !loggedInUser?.role.includes('owner')
                    )
                        return;

                    events.push({
                        id: `linked${value.id}`,
                        groupId: value.id,
                        resourceId: module.id,
                        moduleName: value.module.name,
                        title: `${module.name} - REF: #${value.id}${
                            module.linkType === 'block'
                                ? ' (blocked)'
                                : ` (${value.status})`
                        }`,
                        backgroundColor:
                            module.linkType === 'block'
                                ? theme.palette.calendar.owner
                                : backgroundColor,
                        textColor:
                            module.linkType === 'block' ? 'white' : textColor,
                        borderColor,
                        start,
                        end,
                        extendedProps: {
                            linked: true,
                        },
                    });
                });
            }

            if (childEvents.length > 0 && view.includes('resourceTimeline')) {
                childEvents.map((event) => events.push(event));
            }

            return events;
        });

    return (
        <Box
            sx={{
                height: isMobile ? 550 : 800,
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                pt: 0,
                mt: 0,
                justifyContent: 'flex-start',
                alignItems: 'stretch',
            }}
        >
            <style>{css}</style>
            <Box
                sx={{
                    position: 'relative',
                    flex: 1,
                    minHeight: 0,
                    width: '100%',
                }}
            >
                <FullCalendar
                    schedulerLicenseKey={process.env.REACT_APP_CALENDAR_KEY}
                    themeSystem='bootstrap5'
                    ref={calendarRef}
                    firstDay={1}
                    plugins={[
                        dayGridPlugin,
                        timeGridPlugin,
                        listPlugin,
                        bootstrap5Plugin,
                        interactionPlugin,
                        resourceTimelinePlugin,
                    ]}
                    initialView={view}
                    initialDate={date}
                    resourcesInitiallyExpanded
                    resourceLaneWillUnmount={(arg) =>
                        handleCollapse(arg.resource, true)
                    }
                    resourceLaneDidMount={(arg) =>
                        handleCollapse(arg.resource, false)
                    }
                    datesSet={handleDatesSet}
                    events={getDates}
                    resources={resources}
                    editable
                    selectable
                    selectMirror
                    eventClick={handleEventClick}
                    height='100%'
                    eventContent={EventContent}
                    headerToolbar={{
                        start: isMobile
                            ? 'title'
                            : view.includes('resourceTimeline')
                              ? `${
                                    loggedInUser?.role.includes('owner')
                                        ? 'calendar,resource '
                                        : ''
                                }resourceTimelineDay,resourceTimelineWeek,resourceTimelineMonth`
                              : `${
                                    loggedInUser?.role.includes('owner')
                                        ? 'calendar,resource '
                                        : ''
                                }timeGridDay,timeGridWeek,dayGridMonth,listWeek`,
                        center: isMobile
                            ? `${
                                  loggedInUser?.role.includes('owner')
                                      ? 'calendar,resource'
                                      : ''
                              }`
                            : 'title',
                        end: isMobile
                            ? ''
                            : 'prevYear,prev,today,next,nextYear',
                    }}
                    customButtons={{
                        calendar: {
                            text: 'Calendar',
                            click: (_, el) => changeView('dayGridMonth', el),
                        },
                        resource: {
                            text: 'Timeline',
                            click: (_, el) =>
                                changeView('resourceTimelineMonth', el),
                        },
                    }}
                    footerToolbar={{
                        start: '',
                        center: isMobile
                            ? 'prevYear,prev,today,next,nextYear'
                            : '',
                        end: isMobile
                            ? view.includes('resourceTimeline')
                                ? 'resourceTimelineDay,resourceTimelineWeek,resourceTimelineMonth'
                                : 'timeGridDay,timeGridWeek,dayGridMonth,listWeek'
                            : '',
                    }}
                    buttonText={{
                        today: 'Today',
                        month: 'Month',
                        week: 'Week',
                        day: 'Day',
                        list: 'List',
                    }}
                    resourceAreaHeaderContent='Experiences'
                    eventDidMount={(info: any) => {
                        const { status, type } = info.event.extendedProps;

                        const titleEl = info.el.getElementsByClassName(
                            'fc-list-event-title',
                        )[0];
                        const dotEl =
                            info.el.getElementsByClassName(
                                'fc-list-event-dot',
                            )[0];

                        if (status === 'pending') {
                            if (titleEl) {
                                dotEl.style.borderColor =
                                    theme.palette.calendar.owner;
                            }
                        } else if (status === 'completed') {
                            info.el.style.color =
                                theme.palette.calendar.completed;
                            if (titleEl) {
                                dotEl.style.borderColor =
                                    theme.palette.calendar.completed;
                            }
                        } else if (type === 'property') {
                            info.el.style.color =
                                theme.palette.calendar.property;
                            if (titleEl) {
                                dotEl.style.borderColor =
                                    theme.palette.calendar.property;
                            }
                        } else if (type === 'boat') {
                            if (titleEl) {
                                dotEl.style.borderColor =
                                    theme.palette.calendar.boat;
                            }
                        } else {
                            info.el.style.color =
                                theme.palette.calendar.calendarDefault;
                            if (titleEl) {
                                dotEl.style.borderColor =
                                    theme.palette.calendar.activity;
                            }
                        }
                    }}
                />
                {isCalendarContentLoading ? (
                    <Box
                        sx={{
                            position: 'absolute',
                            inset: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: alpha(
                                theme.palette.background.paper,
                                0.75,
                            ),
                            zIndex: 4,
                        }}
                    >
                        <LoadingComponent />
                    </Box>
                ) : null}
            </Box>
            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={() => {
                    setAnchorEl(null);
                    setSelectedEvent(null);
                }}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
            >
                <Box sx={{ p: 2 }}>
                    <Typography variant='subtitle1' fontWeight={600}>
                        {selectedEvent?.title}
                    </Typography>
                    <Typography variant='body3'>
                        Start: {selectedEvent?.start?.toLocaleString('en-AU')}
                    </Typography>
                    <Typography variant='body3'>
                        End: {selectedEvent?.end?.toLocaleString('en-AU')}
                    </Typography>
                    <Box mt={2}>
                        <Button
                            variant='contained'
                            size='small'
                            onClick={() => handleNavigate(selectedEvent)}
                        >
                            View
                        </Button>
                    </Box>
                </Box>
            </Popover>
        </Box>
    );
}

const EventContent = (arg: EventContentArg) => {
    return (
        <div className='fc-event-main-frame'>
            <div className='fc-event-title-container'>
                <div className='fc-event-title fc-sticky'>
                    {arg.event.extendedProps.linked && (
                        <span
                            className='fc-linked-icon'
                            style={{ marginRight: 4 }}
                        >
                            <Link
                                sx={{
                                    fontSize: 20,
                                    verticalAlign: 'middle',
                                }}
                                aria-hidden
                            />
                        </span>
                    )}
                    {arg.event.title}
                </div>
            </div>
        </div>
    );
};
